// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ColorPickerFieldDefinition } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Icon, { IconPresentationProps, IconWrapper } from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import xHeaderUpdate from "@west/App/Handlers/XNG/Header";
import { CustomAppState } from "@west/App/Store/Reducers";
import Close from "@west/XDL/Icons/Close";
import { color, layoutContainer, xScreen } from "@west/XDL/Utilities";
import { getDataLayer } from "@west/XDL/Utilities/tracking";
import parse, { DomElement } from "html-react-parser";
import React, { FC } from "react";
import { useDispatch } from "react-redux";
import { CSSTransition } from "react-transition-group";
import { css } from "styled-components";

const enum fields {
    message = "message",
    backgroundColor = "backgroundColor",
    color = "color",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.message]: string;
        [fields.backgroundColor]: ColorPickerFieldDefinition;
        [fields.color]: string;
    };
}

type Props = OwnProps;

export interface GlobalWebsiteMessageStyles {
    container?: InjectableCss;
    wrapper?: InjectableCss;
    messageWrapper?: InjectableCss;
    closeClickable?: ClickablePresentationProps;
    closeIcon?: IconPresentationProps;
    message?: TypographyPresentationProps;
}

export const globalWebsiteMessageStyles: GlobalWebsiteMessageStyles = {
    container: {
        css: css`
            .global-message-enter {
                opacity: 0;
            }

            .global-message-enter-active {
                opacity: 1;
            }
            .global-message-exit {
                opacity: 1;
            }

            .global-message-exit-active {
                animation: containerAnimation 500ms ease forwards;
                transform-origin: left;
            }

            @keyframes containerAnimation {
                from {
                    opacity: 1;
                    transform: translate(0, 0);
                }
                to {
                    opacity: 0;
                    transform: translate(0, -50px);
                }
            }
        `,
    },
    wrapper: {
        css: css`
            ${({ backgroundColor }: any) =>
                backgroundColor &&
                css`
                    background: ${backgroundColor};
                `}
        `,
    },
    messageWrapper: {
        css: css`
            position: relative;
            display: flex;
            align-items: center;
            justify-content: center;
            padding-top: 8px;
            padding-bottom: 10px;
            &&& {
                padding-right: 30px;
            }
            ${layoutContainer}
        `,
    },
    closeClickable: {
        css: css`
            position: absolute;
            right: 15px;
            top: 50%;
            transform: translateY(-50%);

            &:focus {
                outline: none;
            }

            &:hover > ${IconWrapper} {
                color: ${color("danger")};
            }
        `,
    },
    closeIcon: {
        src: Close,
        size: 12,
    },
    message: {
        size: 14,
        lineHeight: "14px",
        color: "common.white",
        weight: "bold",
        css: css`
            letter-spacing: 0;
            text-align: center;
            margin-bottom: 0;

            & > ${TypographyStyle} {
                font-size: inherit;
                color: inherit;
                font-weight: inherit;
                line-height: inherit;
                margin-bottom: 0;
            }

            ${xScreen("lte", "xs")} {
                padding: 0 20px 0 0;
                text-align: left;
            }
        `,
    },
};

const styles = globalWebsiteMessageStyles;

const GlobalWebsiteMessage: FC<Props> = ({ fields: { message, backgroundColor, color: colorField } }) => {
    const dispatch = useDispatch();
    const { showGlobalMessage, xGoogleAnalytics } = useAppSelector(state => ({
        showGlobalMessage: (state as CustomAppState).xHeader.showGlobalMessage,
        xGoogleAnalytics: (state as CustomAppState).xGoogleAnalytics,
    }));
    // const containsAnchorTag = () => {
    //     const tempDiv = document.createElement("div");
    //     tempDiv.innerHTML = message;
    //     return !!tempDiv.querySelector("a");
    // };

    const getFirstHref = React.useMemo(() => {
        if (!message) {
            return null;
        }

        let foundHref: string | null = null;

        parse(message, {
            replace: node => {
                if (!foundHref && node.type === "tag" && node.name === "a") {
                    const element = node;
                    foundHref = element.attribs?.href || null;
                }

                return false; // Ensure we return a valid value
            },
        });

        return foundHref;
    }, [message]);

    const onClose = () => {
        dispatch(xHeaderUpdate({ key: "showGlobalMessage", value: false }));
    };
    // XNG-Changes: Google Analytics
    const gaSolutionsButtonClickHandler = () => {
        if (xGoogleAnalytics.trackGoogleAnalytics && xGoogleAnalytics.addPaymentInfoEvent) {
            const dataLayer = getDataLayer();
            if (!dataLayer) {
                return;
            }
            const googleAnalyticsData = {
                event: xGoogleAnalytics.addPaymentInfoEventName,
                click_text: parse(message, parserOptions),
                destination: getFirstHref,
                affiliation: xGoogleAnalytics.affiliation,
                authentication_state: xGoogleAnalytics.authenticationState,
            };

            dataLayer.push(googleAnalyticsData);
        }
    };

    return (
        <StyledWrapper {...styles.container} data-test-selector="global-message-widget">
            <CSSTransition
                in={showGlobalMessage}
                timeout={{
                    enter: 0,
                    exit: 250,
                }}
                unmountOnExit
                classNames="global-message"
            >
                <StyledWrapper {...styles.wrapper} {...{ backgroundColor }}>
                    <StyledWrapper {...styles.messageWrapper}>
                        <Clickable
                            {...styles.closeClickable}
                            onClick={onClose}
                            aria-label="close"
                            data-test-selector="global_site_message_close"
                        >
                            <Icon {...styles.closeIcon} color={colorField || "common.white"} />
                        </Clickable>
                        <Typography
                            {...styles.message}
                            color={colorField || "common.white"}
                            data-test-selector="global_site_message"
                            as="div"
                            onClick={() => {
                                getFirstHref ? gaSolutionsButtonClickHandler() : undefined;
                            }}
                        >
                            {parse(message, parserOptions)}
                        </Typography>
                    </StyledWrapper>
                </StyledWrapper>
            </CSSTransition>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: GlobalWebsiteMessage,
    definition: {
        group: "Header",
        displayName: "Global Website Message",
        allowedContexts: ["Header"],
        fieldDefinitions: [
            {
                name: fields.message,
                displayName: "Global Message Content",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "General",
                placeholder: "Add your message here",
                isRequired: true,
            },
            {
                name: fields.backgroundColor,
                displayName: "Background Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
                popoverLocation: "top",
            },
            {
                name: fields.color,
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                fieldType: "General",
                popoverLocation: "top",
            },
        ],
    },
};

export default widgetModule;
